import { useState, useRef } from "react"
import { motion, AnimatePresence } from "framer-motion"
import {
  Box,
  Flex,
  Input,
  Select,
  Text,
  Heading,
  useOutsideClick,
  Collapse,
  Divider,
  useDisclosure,
} from "@chakra-ui/react"
import { FaFilter } from "react-icons/fa"
import { ExportIcon } from "@phosphor-icons/react"
import { ArrowDownIcon2 } from "@/styles/icons/ArrowDownIcon2"
import { useCharge } from "@/hooks/useCharge"
import { showToast } from "@/utils";
import { useAsyncFunction } from "@/hooks/useAsyncFunction"
import { useChargeFilter } from "@/hooks"
import { FilterState } from "@/models"
import { Badge, Button, Icon, Modal, Search, Tooltip } from "barte-design-system"
interface ChargeTableFilterProps {
  onFilterChange: (filterState: FilterState) => void
}

export function ChargeTableFilter({ onFilterChange }: ChargeTableFilterProps) {
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [activeFilters, setActiveFilters] = useState<string[]>([])
  const filterRef = useRef<HTMLDivElement>(null)
  const downloadRef = useRef<HTMLDivElement>(null)
  const inputEmailRef = useRef<HTMLInputElement>(null)
  const { downloadCSV } = useCharge();
  const { isOpen: isDownloadOpen, onOpen: openDownload, onClose: closeDownload } = useDisclosure();

  const user = JSON.parse(localStorage.getItem("barte-user-info") || "{}");
  const { execute, loading } = useAsyncFunction(downloadCSV);
  const {
    filterState,
    setFilterState,
    clearFilterState,
    searchTypeOptions,
    filterOptions,
    searchTerm,
    searchType,
    setSearchTerm,
    setSearchType,
    setHasAppliedFilters,
  } = useChargeFilter();

  const appliedCount = [
    filterState.status && filterState.status !== "ALL",
    filterState.expirationDateInitial || filterState.expirationDateFinal,
    filterState.paidDateInitial || filterState.paidDateFinal,
    filterState.customerName,
    filterState.customerDocument,
    filterState.customerEmail,
    filterState.title,
    filterState.paymentLink,
    filterState.valueMin || filterState.valueMax,
    filterState.paymentMethod && filterState.paymentMethod !== "ALL",
  ].filter(Boolean).length

  useOutsideClick({
    ref: filterRef,
    handler: () => setIsFilterOpen(false),
  })

  const toggleFilter = () => {
    setIsFilterOpen(!isFilterOpen)
  }

  const toggleFilterOption = (id: string) => {
    if (activeFilters.includes(id)) {
      setActiveFilters(activeFilters.filter((filterId) => filterId !== id))
    } else {
      setActiveFilters([...activeFilters, id])
    }
  }

  const handleClearFilters = () => {
    setSearchTerm("");
    setFilterState(clearFilterState);
    setHasAppliedFilters(false);
    setIsFilterOpen(false);
    onFilterChange(clearFilterState);
  };

  const handleFilterChange = (id: string, value: any) => {
    setFilterState(prev => ({
      ...prev,
      [id]: value,
    }))
  }

  const applyFilters = () => {
    const hasSearchTerm = searchTerm.trim() !== ""
    const hasFilterChanges = JSON.stringify(filterState) !== JSON.stringify(clearFilterState)
    setHasAppliedFilters(hasSearchTerm || hasFilterChanges)
    setIsFilterOpen(false)
    onFilterChange({ ...filterState, page: 1 })
  }

  const handleChangeSearchType = (value?: string) => {
    setSearchType(value);
    handleClearFilters();
  }

  const handleConfirmDownload = async () => {
    try {
      const formatData = {
        expirationDateInitial: filterState?.expirationDateInitial ? filterState?.expirationDateInitial.toString() : "",
        expirationDateFinal: filterState?.expirationDateFinal ? filterState?.expirationDateFinal.toString() : "",
        recipient: inputEmailRef.current?.value
      }
      await execute(formatData);
      showToast({
        type: "success",
        message: "Relatório gerado com sucesso. Será enviado para o email informado.",
      });
    } catch {
      showToast({
        type: "error",
        message: "Falha ao gerar relatório.",
      });
    } finally {
      closeDownload();
    }
  };

  return (
    <>
      <Modal
        isOpen={isDownloadOpen}
        title="Baixar relatório .csv"
        onClose={closeDownload}
        footer={{
          primaryButton: {
            children: loading ? "Processando..." : "Ok",
            onClick: handleConfirmDownload,
            loading,
            variant: "outline",
          },
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
          <p>
            O relatório será gerado com os filtros que estão aplicados na visualização atual e ficará disponível para download na Central de notificações.
          </p>
          <Input
            type="email"
            placeholder="seu-email@dominio.com"
            name="email"
            ref={inputEmailRef}
            defaultValue={user?.email}
            _focus={{ borderColor: "pink.500" }}
          />
        </div>
      </Modal>
      <Flex
        w="100%"
      >
        <Flex
          w="100%"
          flexDir="column"
          gap="2"
        >
          <Flex
            align="center"
            gap="2"
          >
            <Search
              value={searchTerm}
              onChange={(value) => {
                setSearchTerm(value);
                handleFilterChange(searchType, value === "" ? null : value);
              }}
              onSearch={applyFilters}
              categories={searchTypeOptions}
              selectedCategory={searchType}
              onCategoryChange={handleChangeSearchType}
              expanded
            />
            <Box ref={filterRef}>
              <Box position="relative" display="inline-flex">
                <Button
                  onClick={toggleFilter}
                  variant="outline"
                  iconLeft={<FaFilter size="14px" color="currentColor" />}
                  size="sm"
                  iconRight={appliedCount > 0 ? <Badge tone="brand" size="base">{appliedCount}</Badge> : null}
                >
                    <Text
                      fontSize="16px"
                      fontWeight="400"
                    >
                      Filtro
                    </Text>
                </Button>
                
              </Box>

              <AnimatePresence>
                {isFilterOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: 0 }}
                    exit={{ opacity: 0, y: -10, height: 0 }}
                    transition={{ duration: 0.2 }}
                    style={{
                      position: "relative",
                    }}
                  >
                    <Box
                      position="absolute"
                      right="0"
                      mt="2"
                      w="72"
                      bg="white"
                      borderWidth="1px"
                      borderRadius="md"
                      boxShadow="lg"
                      zIndex="20"
                      overflow="hidden"
                    >
                      <Flex justify="space-between" align="center" p="4" borderBottomWidth="1px">
                        <Heading as="h3" size="sm" fontWeight="medium">
                          Filtros
                        </Heading>
                        <Button
                          variant="link"
                          size="sm"
                          onClick={handleClearFilters}
                        >
                          Limpar Todos
                        </Button>
                      </Flex>

                      <Box p="4" maxH="96" overflowY="auto">
                        {filterOptions.map((option) => (
                          <Box key={option.id} mb="4">
                            <Flex
                              align="center"
                              justify="space-between"
                              cursor="pointer"
                              mb="2"
                              onClick={() => toggleFilterOption(option.id)}
                            >
                              <Flex align="center">
                                <Text mr="2">
                                  {option.icon}
                                </Text>
                                <Text>{option.label}</Text>
                              </Flex>
                              <Box
                                transition="transform 0.2s"
                                transform={
                                  activeFilters.includes(option.id)
                                    ? "rotate(180deg)"
                                    : "rotate(0deg)"
                                }
                              >
                                <ArrowDownIcon2 />
                              </Box>
                            </Flex>

                            <Collapse in={activeFilters.includes(option.id)}>
                              {option.type === "date" && (
                                <Flex pl="6" mt="2" gap="2">
                                  <Input
                                    type="date"
                                    size="sm"
                                    value={option.id === "data-pagamento"
                                      ? filterState.paidDateInitial?.toString()
                                      : filterState.expirationDateInitial?.toString()}
                                    onChange={(e) => {
                                      const value = e.target.value || null
                                      if (option.id === "data-pagamento") {
                                        handleFilterChange("paidDateInitial", value)
                                      } else {
                                        handleFilterChange("expirationDateInitial", value)
                                      }
                                    }}
                                    _focus={{
                                      border: "1px solid",
                                      borderColor: "pink.500"
                                    }}
                                  />
                                  <Input
                                    type="date"
                                    size="sm"
                                    value={option.id === "data-pagamento"
                                      ? filterState.paidDateFinal?.toString()
                                      : filterState.expirationDateFinal?.toString()}
                                    onChange={(e) => {
                                      const value = e.target.value || null
                                      if (option.id === "data-pagamento") {
                                        handleFilterChange("paidDateFinal", value)
                                      } else {
                                        handleFilterChange("expirationDateFinal", value)
                                      }
                                    }}
                                    _focus={{
                                      border: "1px solid",
                                      borderColor: "pink.500"
                                    }}
                                  />
                                </Flex>
                              )}

                              {option.type === "range" && (
                                <Flex pl="6" mt="2" gap="2">
                                  <Input
                                    type="number"
                                    placeholder="Min"
                                    size="sm"
                                    value={filterState.valueMin || ""}
                                    onChange={(e) => handleFilterChange("valueMin", parseFloat(e.target.value.replaceAll(",", ".")))}
                                    _focus={{
                                      border: "1px solid",
                                      borderColor: "pink.500"
                                    }}
                                  />
                                  <Input
                                    type="number"
                                    placeholder="Max"
                                    size="sm"
                                    value={filterState.valueMax || ""}
                                    onChange={(e) => handleFilterChange("valueMax", parseFloat(e.target.value.replaceAll(",", ".")))}
                                    _focus={{
                                      border: "1px solid",
                                      borderColor: "pink.500"
                                    }}
                                  />
                                </Flex>
                              )}

                              {option.type === "text" && (
                                <Box pl="6" mt="2">
                                  <Input
                                    size="sm"
                                    value={
                                      option.id === "cliente" ? filterState.customerName || "" :
                                        option.id === "documento" ? filterState.customerDocument || "" :
                                          option.id === "email" ? filterState.customerEmail || "" :
                                            option.id === "titulo" ? filterState.title || "" :
                                              option.id === "link" ? filterState.paymentLink || "" :
                                                ""
                                    }
                                    onChange={(e) => {
                                      const value = e.target.value || null
                                      switch (option.id) {
                                        case "cliente":
                                          handleFilterChange("customerName", value)
                                          break
                                        case "documento":
                                          handleFilterChange("customerDocument", value)
                                          break
                                        case "email":
                                          handleFilterChange("customerEmail", value)
                                          break
                                        case "titulo":
                                          handleFilterChange("title", value)
                                          break
                                        case "link":
                                          handleFilterChange("paymentLink", value)
                                          break
                                      }
                                    }}
                                  />
                                </Box>
                              )}

                              {!option.type && option.options && (
                                <Box pl="6" mt="2">
                                  <Select
                                    size="sm"
                                    value={filterState[`${option.id}`] || ""}
                                    onChange={(e) => handleFilterChange(option.id, e.target.value)}
                                    _focus={{
                                      border: "1px solid",
                                      borderColor: "pink.500"
                                    }}
                                  >
                                    {option.options.map((opt) => (
                                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                  </Select>
                                </Box>
                              )}
                            </Collapse>
                          </Box>
                        ))}
                      </Box>

                      <Divider />
                      <Flex p="4" justify="flex-end">
                        <Button
                          variant="fill"
                          size="sm"
                          onClick={applyFilters}
                        >
                          Aplicar Filtros
                        </Button>
                      </Flex>
                    </Box>
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>
            <Box ml="auto">
              <Tooltip placement="top" isDisabled={filterState?.expirationDateInitial === null || filterState?.expirationDateFinal === null ? false : true} label="Selecione uma data de vencimento para poder gerar o relatório de cobranças no formato CSV.">
                <Box ref={downloadRef}>
                  <Button
                    variant="outline"
                    onClick={openDownload}
                    disabled={filterState?.expirationDateInitial === null || filterState?.expirationDateFinal === null}
                  >
                    <Flex align="center" gap="2">
                      <Icon icon={ExportIcon} size={16} />
                      <Text>
                        Exportar CSV
                      </Text>
                    </Flex>
                  </Button>
                </Box>
              </Tooltip>
            </Box>
          </Flex>
        </Flex>
      </Flex>
    </>

  )
}